/**
 * DividerPoint — Reusable divider with animated center dot
 *
 * A decorative divider element with:
 * - Two gradient lines fading to transparent on edges
 * - Animated pulsing dot in the center (uses .animate-pulse-slow utility)
 * - Optional visibility animation
 * - Customizable size and spacing
 *
 * DS note: `pulse-slow` keyframes + utility live in `tailwind.config.css`
 * (single source of truth — no component-local <style jsx>).
 */
export default function DividerPoint({
  // Size variants
  size = 'md',           // sm | md | lg
  // Spacing
  spacing = 'md',        // sm | md | lg
  // Animation
  animate = true,        // Enable pulse animation
  // Visibility
  isVisible = true,      // For fade-in animation
  className = '',        // Additional classes
}) {
  // Size mapping
  const sizes = {
    sm: { line: 'w-12 md:w-16', dot: 'w-2 h-2' },
    md: { line: 'w-16 md:w-24', dot: 'w-2.5 h-2.5' },
    lg: { line: 'w-20 md:w-32', dot: 'w-3 h-3' },
  }

  // Spacing mapping
  const spacings = {
    sm: 'mt-6 md:mt-8',
    md: 'mt-10 md:mt-12',
    lg: 'mt-12 md:mt-16',
  }

  const currentSize = sizes[size] || sizes.md
  const currentSpacing = spacings[spacing] || spacings.md

  return (
    <div
      className={`flex items-center justify-center gap-4 transition-all duration-1000 ${currentSpacing} ${
        isVisible ? 'opacity-100' : 'opacity-0'
      } ${className}`}
    >
      {/* Left Line */}
      <div className={`h-px ${currentSize.line} bg-gradient-to-r from-transparent via-[var(--color-secondary)]/70 to-transparent`} />

      {/* Center Dot */}
      <div className={`rounded-full bg-color-primary ${currentSize.dot} ${animate ? 'animate-pulse-slow' : ''}`} />

      {/* Right Line */}
      <div className={`h-px ${currentSize.line} bg-gradient-to-r from-transparent via-[var(--color-secondary)]/70 to-transparent`} />
    </div>
  )
}
